import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Stack,
  Grid,
  Drawer,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Link,
  CircularProgress,
  Alert,
  Button,
  TablePagination,
  Checkbox,
  Tooltip,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolIcon from '@mui/icons-material/School';
import apiService, {
  BlockFrameUrl,
  CommitPgmFixtureFrameResult,
  MentionOrigin,
  PgmCueKind,
  PgmFixtureSelection,
  PodcastLearningExampleRow,
  PodcastLearningProfileCategory,
} from '../services/api';
import {
  PODCAST_LEARNINGS_PAGE_SIZE_OPTIONS,
  usePodcastLearningsStore,
  type PodcastLearningsEvidenceFilter,
  type PodcastLearningsIncludeFilter,
} from '../stores/podcastLearningsStore';
import { ROUTES_NAMES } from '../components/Layout';
import { REJECT_MENTION_REASON_PRESETS } from '../utils/rejectMentionReasonPresets';
import {
  VISUAL_CUE_OPTIONS,
  type VisualCue,
  visualCueLabel,
} from '../utils/visualCueLabels';

const OUTCOME_COLOR: Record<string, 'success' | 'error' | 'default'> = {
  confirmed: 'success',
  rejected: 'error',
};

const MENTION_ORIGIN_COLOR: Record<
  MentionOrigin,
  'default' | 'info' | 'warning' | 'secondary'
> = {
  ai_detected: 'info',
  human_edited: 'warning',
  human_added: 'secondary',
};

const MENTION_ORIGIN_FILTER_OPTIONS: { value: '' | MentionOrigin; label: string }[] = [
  { value: '', label: 'All origins' },
  { value: 'ai_detected', label: 'AI detected' },
  { value: 'human_edited', label: 'Human edited' },
  { value: 'human_added', label: 'Human added' },
];

function MentionOriginChip({
  origin,
  label,
  correctedFields,
}: {
  origin?: MentionOrigin;
  label?: string;
  correctedFields?: string[];
}) {
  if (!origin) return null;
  const tooltip =
    correctedFields && correctedFields.length > 0
      ? `Edited fields: ${correctedFields.join(', ')}`
      : origin === 'ai_detected'
        ? 'Originally detected by AI without human edits'
        : origin === 'human_added'
          ? 'Manually added by a reviewer'
          : 'AI detection corrected by a reviewer';
  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        variant="outlined"
        color={MENTION_ORIGIN_COLOR[origin]}
        label={label ?? origin}
      />
    </Tooltip>
  );
}

const PGM_CUE_OPTIONS: { value: PgmCueKind; label: string }[] = [
  { value: 'curtain', label: 'Curtain' },
  { value: 'banner', label: 'Banner' },
  { value: 'lateral_banner', label: 'Lateral banner' },
];

/** Wall-clock stamp matching pipelines FrameSampler.formatFrameTimeLabel */
function formatFrameTimeStamp(tsec: number): string {
  const t = Math.max(0, Math.floor(tsec));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return `${String(h).padStart(2, '0')}-${String(m).padStart(2, '0')}-${String(s).padStart(2, '0')}`;
}

function buildPgmSourceKey(contentId: string, tsec: number): string {
  return `${contentId}:${formatFrameTimeStamp(tsec)}`;
}

function countCommittedPgmFrames(contentId: string, sourceKeys: string[]): number {
  const prefix = `${contentId}:`;
  return sourceKeys.filter((key) => key.startsWith(prefix)).length;
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function StatCard({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      ) : null}
    </Paper>
  );
}

function CategoryTable({ rows }: { rows: PodcastLearningProfileCategory[] }) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No category rollups yet for this podcast.
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Failure mode</TableCell>
          <TableCell align="right">Rejected</TableCell>
          <TableCell align="right">Confirmed</TableCell>
          <TableCell>Last reviewed</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows
          .sort((a, b) => b.rejectedCount + b.confirmedCount - (a.rejectedCount + a.confirmedCount))
          .map((row) => (
            <TableRow key={row.failureMode}>
              <TableCell>{row.failureModeLabel}</TableCell>
              <TableCell align="right">{row.rejectedCount}</TableCell>
              <TableCell align="right">{row.confirmedCount}</TableCell>
              <TableCell>
                {row.lastReviewedAt
                  ? new Date(row.lastReviewedAt).toLocaleDateString('es-CL')
                  : '—'}
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
}

function initialReasonPreset(ex: PodcastLearningExampleRow): string {
  if (ex.reasonPreset) return ex.reasonPreset;
  if (ex.reasonText && (REJECT_MENTION_REASON_PRESETS as readonly string[]).includes(ex.reasonText)) {
    return ex.reasonText;
  }
  return '';
}

function initialReasonNotes(ex: PodcastLearningExampleRow): string {
  const preset = initialReasonPreset(ex);
  if (ex.reasonText && ex.reasonText !== preset) return ex.reasonText;
  return '';
}

function initialVisualCue(
  ex: PodcastLearningExampleRow,
  mention?: { visualCue?: string } | null,
): VisualCue | '' {
  const raw = ex.visualCue ?? mention?.visualCue;
  if (raw === 'banner' || raw === 'curtain' || raw === 'logo_overlay' || raw === 'lateral_banner') {
    return raw;
  }
  return '';
}

function segmentOverlapsWindow(
  seg: { start: number; end: number },
  windowStart: number,
  windowEnd: number,
): boolean {
  return seg.end >= windowStart && seg.start <= windowEnd;
}

type FrameEvidenceItem = {
  key: string;
  url: string;
  label: string;
  tsec?: number;
  isRepresentative?: boolean;
};

function collectFrameEvidenceItems(args: {
  learningFrameUrl?: string;
  learningFrameUrls?: string[];
  mentionFrameUrl?: string;
  blockFrameUrls?: BlockFrameUrl[];
}): FrameEvidenceItem[] {
  const seen = new Set<string>();
  const items: FrameEvidenceItem[] = [];

  const push = (item: FrameEvidenceItem) => {
    if (!item.url || seen.has(item.url)) return;
    seen.add(item.url);
    items.push(item);
  };

  for (const frame of args.blockFrameUrls ?? []) {
    if (!frame.url) continue;
    push({
      key: `block-${frame.tsec}`,
      url: frame.url,
      label: formatTime(frame.tsec),
      tsec: frame.tsec,
      isRepresentative: frame.isRepresentative,
    });
  }

  const learningUrls =
    args.learningFrameUrls?.filter(Boolean) ??
    (args.learningFrameUrl ? [args.learningFrameUrl] : []);
  learningUrls.forEach((url, index) => {
    push({
      key: `learning-${index}`,
      url,
      label: learningUrls.length > 1 ? `Learning frame ${index + 1}` : 'Learning frame',
    });
  });

  if (args.mentionFrameUrl) {
    push({
      key: 'mention',
      url: args.mentionFrameUrl,
      label: 'Mention frame',
    });
  }

  return items;
}

function FrameEvidenceGallery({
  frames,
  contentUrl,
}: {
  frames: FrameEvidenceItem[];
  contentUrl?: string | null;
}) {
  if (frames.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: 1.5,
        mt: 1,
      }}
    >
      {frames.map((frame) => (
        <Box key={frame.key} sx={{ textAlign: 'center' }}>
          <Box
            component="a"
            href={frame.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'block', textDecoration: 'none' }}
          >
            <Box
              component="img"
              src={frame.url}
              alt={frame.label}
              sx={{
                display: 'block',
                width: '100%',
                height: 120,
                objectFit: 'cover',
                borderRadius: 1,
                border: 2,
                borderColor: frame.isRepresentative ? 'primary.main' : 'divider',
                cursor: 'pointer',
              }}
            />
          </Box>
          <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
              {frame.label}
            </Typography>
            {frame.isRepresentative ? (
              <Chip label="OCR" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
            ) : null}
            {contentUrl && frame.tsec != null ? (
              <Link
                href={`${contentUrl}${contentUrl.includes('?') ? '&' : '?'}t=${Math.floor(frame.tsec)}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: 10 }}
              >
                YT
              </Link>
            ) : null}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

function SelectableFrameEvidenceGallery({
  frames,
  contentUrl,
  contentId,
  mentionId,
  learningExampleId,
  pgmSelections,
  committedSourceKeys,
  pgmCue,
  onPgmDataChanged,
}: {
  frames: FrameEvidenceItem[];
  contentUrl?: string | null;
  contentId: string;
  mentionId: string;
  learningExampleId: string;
  pgmSelections: PgmFixtureSelection[];
  committedSourceKeys: Set<string>;
  pgmCue: PgmCueKind;
  onPgmDataChanged: () => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const selectableFrames = frames.filter((frame) => frame.tsec != null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [lastResults, setLastResults] = useState<CommitPgmFixtureFrameResult[] | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [optimisticCommitted, setOptimisticCommitted] = useState<Set<string>>(new Set());

  const allCommittedKeys = useMemo(() => {
    const merged = new Set(committedSourceKeys);
    optimisticCommitted.forEach((key) => merged.add(key));
    return merged;
  }, [committedSourceKeys, optimisticCommitted]);

  const savedTsecs = useMemo(() => {
    return new Set(pgmSelections.filter((s) => !s.committedCaseId).map((s) => s.tsec));
  }, [pgmSelections]);

  useEffect(() => {
    const initial = new Set<string>();
    for (const frame of selectableFrames) {
      if (frame.tsec != null && savedTsecs.has(frame.tsec)) {
        initial.add(frame.key);
      }
    }
    setSelectedKeys(initial);
    setLastResults(null);
    setSaveOk(false);
    setOptimisticCommitted(new Set());
  }, [learningExampleId, pgmCue, savedTsecs, selectableFrames.length]);

  const selectedTsecs = () =>
    selectableFrames.filter((frame) => selectedKeys.has(frame.key)).map((frame) => frame.tsec!);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiService.savePgmFixtureSelections({
        cue: pgmCue,
        learningExampleId,
        mentionId,
        contentId,
        tsecs: selectedTsecs(),
      }),
    onSuccess: async () => {
      setSaveOk(true);
      await onPgmDataChanged();
    },
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      await apiService.savePgmFixtureSelections({
        cue: pgmCue,
        learningExampleId,
        mentionId,
        contentId,
        tsecs: selectedTsecs(),
      });
      return apiService.commitPgmFixtures({
        cue: pgmCue,
        pool: 'positive',
        frames: selectedTsecs().map((tsec) => ({
          learningExampleId,
          mentionId,
          contentId,
          tsec,
        })),
      });
    },
    onSuccess: async (data) => {
      setLastResults(data.results);
      const newlyCommitted = new Set<string>();
      for (const result of data.results) {
        if (result.ok) {
          newlyCommitted.add(result.sourceKey);
        }
      }
      if (newlyCommitted.size > 0) {
        setOptimisticCommitted((prev) => new Set([...Array.from(prev), ...Array.from(newlyCommitted)]));
      }
      if (data.index) {
        queryClient.setQueryData(['pgm-fixture-status', pgmCue], data.index);
      }
      await queryClient.invalidateQueries({ queryKey: ['pgm-fixture-status', pgmCue] });
      await queryClient.invalidateQueries({ queryKey: ['podcast-learnings-pgm-examples'] });
      await onPgmDataChanged();
    },
  });

  const isFrameCommitted = (frame: FrameEvidenceItem): boolean => {
    if (frame.tsec == null) return false;
    return allCommittedKeys.has(buildPgmSourceKey(contentId, frame.tsec));
  };

  const toggleFrame = (frame: FrameEvidenceItem) => {
    if (frame.tsec == null || isFrameCommitted(frame)) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(frame.key)) next.delete(frame.key);
      else next.add(frame.key);
      return next;
    });
  };

  if (selectableFrames.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        No block frames with timestamps. Run{' '}
        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
          yarn explorer:long:visual-analyze --content-id {contentId}
        </Typography>{' '}
        on the episode first.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 1.5,
        }}
      >
        {selectableFrames.map((frame) => {
          const committed = isFrameCommitted(frame);
          const selected = selectedKeys.has(frame.key);
          return (
            <Box key={frame.key} sx={{ textAlign: 'center' }}>
              <Tooltip
                title={
                  committed
                    ? 'Already in PGM positive pool'
                    : selected
                      ? 'Click to deselect'
                      : 'Select for PGM fixture'
                }
              >
                <Box
                  sx={{
                    position: 'relative',
                    cursor: committed ? 'not-allowed' : 'pointer',
                    opacity: committed ? 0.55 : 1,
                  }}
                  onClick={() => toggleFrame(frame)}
                >
                  <Checkbox
                    size="small"
                    checked={committed || selected}
                    disabled={committed}
                    sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1, p: 0.25 }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleFrame(frame)}
                  />
                  <Box
                    component="img"
                    src={frame.url}
                    alt={frame.label}
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: 2,
                      borderColor: committed
                        ? 'success.main'
                        : selected
                          ? 'primary.main'
                          : frame.isRepresentative
                            ? 'info.main'
                            : 'divider',
                    }}
                  />
                </Box>
              </Tooltip>
              <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
                  {frame.label}
                </Typography>
                {committed ? (
                  <Chip label="PGM" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                ) : selected && savedTsecs.has(frame.tsec!) ? (
                  <Chip label="saved" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                ) : null}
                {contentUrl && frame.tsec != null ? (
                  <Link
                    href={`${contentUrl}${contentUrl.includes('?') ? '&' : '?'}t=${Math.floor(frame.tsec)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: 10 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    YT
                  </Link>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Box>

      <Stack spacing={1} sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="small"
            disabled={saveMutation.isPending || addMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : `Save selection (${selectedKeys.size})`}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={selectedKeys.size === 0 || addMutation.isPending || saveMutation.isPending}
            onClick={() => addMutation.mutate()}
          >
            {addMutation.isPending
              ? 'Committing…'
              : `Commit ${selectedKeys.size} frame(s) (${pgmCue})`}
          </Button>
        </Stack>

        {saveOk ? (
          <Alert severity="info" icon={false}>
            Selection saved to MongoDB (<Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>pgm_fixture_selections</Typography>)
          </Alert>
        ) : null}

        {saveMutation.isError ? (
          <Alert severity="error">Could not save selection.</Alert>
        ) : null}

        {addMutation.isError ? (
          <Alert severity="error">Could not commit selection.</Alert>
        ) : null}

        {lastResults && lastResults.length > 0 ? (
          <Alert severity={lastResults.every((r) => r.ok) ? 'success' : 'warning'}>
            <Stack spacing={0.5}>
              {lastResults.map((result) => (
                <Typography key={`${result.contentId}-${result.tsec}`} variant="body2">
                  {formatTime(result.tsec)}: {result.ok ? 'committed' : result.reason ?? 'failed'}
                </Typography>
              ))}
            </Stack>
          </Alert>
        ) : null}
      </Stack>
    </Box>
  );
}

function TranscriptEvidenceBlock({
  segments,
  windowStart,
  windowEnd,
  fallbackText,
}: {
  segments: Array<{ start: number; end: number; text: string }>;
  windowStart: number;
  windowEnd: number;
  fallbackText?: string;
}) {
  if (segments.length === 0 && !fallbackText) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        Transcript · mention {formatTime(windowStart)}–{formatTime(windowEnd)}
      </Typography>
      {segments.length > 0 ? (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {segments.map((seg, i) => {
            const inWindow = segmentOverlapsWindow(seg, windowStart, windowEnd);
            return (
              <Box
                key={`${seg.start}-${seg.end}-${i}`}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: inWindow ? 'action.selected' : 'action.hover',
                  border: 1,
                  borderColor: inWindow ? 'primary.light' : 'divider',
                }}
              >
                <Typography
                  variant="caption"
                  color={inWindow ? 'primary.main' : 'text.secondary'}
                  display="block"
                  sx={{ fontFamily: 'monospace', mb: 0.25 }}
                >
                  [{formatTime(seg.start)}–{formatTime(seg.end)}]
                  {inWindow ? ' · in mention window' : ''}
                </Typography>
                <Typography variant="body2">{seg.text}</Typography>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
          "{fallbackText}"
        </Typography>
      )}
    </Paper>
  );
}

function ExampleDetailDrawer({
  exampleId,
  onClose,
  pgmCue,
  onPgmFixturesChanged,
}: {
  exampleId: string | null;
  onClose: () => void;
  pgmCue?: PgmCueKind;
  onPgmFixturesChanged?: () => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['podcast-learning-example', exampleId, pgmCue],
    queryFn: () => apiService.getPodcastLearningExampleDetail(exampleId!, pgmCue ? { pgmCue } : undefined),
    enabled: Boolean(exampleId),
  });

  const ex = data?.example;
  const mention = data?.mention;
  const transcriptSegments = data?.transcriptSegments ?? [];
  const evidenceWindow = data?.evidenceWindow ?? {
    start: ex?.segmentStart ?? 0,
    end: ex?.segmentEnd ?? 0,
  };
  const pgmCommittedSourceKeys = useMemo(
    () => new Set(data?.pgmFixtures?.committedSourceKeys ?? []),
    [data?.pgmFixtures?.committedSourceKeys],
  );
  const pgmSelections = data?.pgmFixtures?.selections ?? [];

  const refreshExampleDetail = async () => {
    await queryClient.invalidateQueries({ queryKey: ['podcast-learning-example', exampleId, pgmCue] });
    await onPgmFixturesChanged?.();
  };

  const [draftPreset, setDraftPreset] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftInclude, setDraftInclude] = useState(false);
  const [draftExclude, setDraftExclude] = useState(false);
  const [draftVisualCue, setDraftVisualCue] = useState<VisualCue | ''>('');

  useEffect(() => {
    if (!ex) return;
    setDraftPreset(initialReasonPreset(ex));
    setDraftNotes(initialReasonNotes(ex));
    setDraftInclude(Boolean(ex.includeInAlgorithm));
    setDraftExclude(Boolean(ex.excludeFromLearning));
    setDraftVisualCue(initialVisualCue(ex, mention));
  }, [ex, mention]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: {
        includeInAlgorithm: boolean;
        excludeFromLearning: boolean;
        reasonPreset?: string | null;
        reasonText?: string | null;
        visualCue?: string | null;
      } = { includeInAlgorithm: draftInclude, excludeFromLearning: draftExclude };
      if (ex!.outcome === 'rejected') {
        payload.reasonPreset = draftPreset || null;
        payload.reasonText = draftNotes.trim() || draftPreset || null;
      }
      const initialCue = initialVisualCue(ex!, mention);
      if (draftVisualCue !== initialCue) {
        payload.visualCue = draftVisualCue || null;
      }
      return apiService.updatePodcastLearningExample(ex!._id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcast-learning-example', exampleId, pgmCue] });
      queryClient.invalidateQueries({ queryKey: ['podcast-learnings-examples'] });
      queryClient.invalidateQueries({ queryKey: ['podcast-learnings-stats'] });
      queryClient.invalidateQueries({ queryKey: ['podcast-learnings-profile'] });
    },
  });

  const isDirty = useMemo(() => {
    if (!ex) return false;
    const initialCue = initialVisualCue(ex, mention);
    return (
      draftInclude !== Boolean(ex.includeInAlgorithm) ||
      draftExclude !== Boolean(ex.excludeFromLearning) ||
      draftVisualCue !== initialCue ||
      (ex.outcome === 'rejected' &&
        (draftPreset !== initialReasonPreset(ex) || draftNotes !== initialReasonNotes(ex)))
    );
  }, [ex, mention, draftInclude, draftExclude, draftVisualCue, draftPreset, draftNotes]);

  const frameEvidenceItems = useMemo(
    () =>
      collectFrameEvidenceItems({
        learningFrameUrl: ex?.frameUrl,
        learningFrameUrls: ex?.frameUrls,
        mentionFrameUrl: mention?.frameUrl,
        blockFrameUrls: mention?.blockFrameUrls,
      }),
    [ex?.frameUrl, ex?.frameUrls, mention?.frameUrl, mention?.blockFrameUrls],
  );

  return (
    <Drawer anchor="right" open={Boolean(exampleId)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Learning example</Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        {isLoading || !ex ? (
          <CircularProgress size={28} />
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={ex.outcome} color={OUTCOME_COLOR[ex.outcome] ?? 'default'} />
              <MentionOriginChip
                origin={ex.mentionOrigin}
                label={ex.mentionOriginLabel}
                correctedFields={ex.correctedFields}
              />
              <Chip size="small" label={ex.flowLabel ?? ex.flow} variant="outlined" />
              <Chip size="small" label={ex.failureModeLabel ?? ex.failureMode} variant="outlined" />
            </Stack>

            <Box>
              <Typography variant="subtitle2">{ex.brandName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {ex.podcastTitle ?? ex.podcastId} · {formatTime(ex.segmentStart)}
              </Typography>
              {ex.contentTitle ? (
                <Typography variant="body2" color="text.secondary">
                  {ex.contentTitle}
                </Typography>
              ) : null}
            </Box>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Visual cue
              </Typography>
              <TextField
                select
                label="Visual cue"
                size="small"
                fullWidth
                value={draftVisualCue}
                onChange={(e) => setDraftVisualCue(e.target.value as VisualCue | '')}
                disabled={saveMutation.isPending}
                helperText={
                  draftVisualCue
                    ? `Updates learning example and linked mention (${visualCueLabel(draftVisualCue)})`
                    : 'Clears visual cue on learning example and linked mention'
                }
              >
                {VISUAL_CUE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.label} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Algorithm evidence
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draftExclude}
                    disabled={saveMutation.isPending}
                    onChange={(e) => setDraftExclude(e.target.checked)}
                  />
                }
                label="Exclude from auto learning memory"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Drops this example from the auto learning-memory build (junk / not learnable).
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draftInclude}
                    disabled={saveMutation.isPending}
                    onChange={(e) => setDraftInclude(e.target.checked)}
                  />
                }
                label="Include in algorithm prompts (manual path)"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Legacy manual control path. Prod uses auto learning memory + exclude above.
              </Typography>
            </Paper>

            {ex.outcome === 'rejected' ? (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Reject reason
                </Typography>
                <Stack spacing={1.5}>
                  <TextField
                    select
                    label="Reason preset"
                    size="small"
                    fullWidth
                    value={draftPreset}
                    onChange={(e) => setDraftPreset(e.target.value)}
                  >
                    <MenuItem value="">Custom / none</MenuItem>
                    {REJECT_MENTION_REASON_PRESETS.map((preset) => (
                      <MenuItem key={preset} value={preset}>
                        {preset}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Notes (optional)"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={draftNotes}
                    onChange={(e) => setDraftNotes(e.target.value)}
                    placeholder="Extra detail for the learning example…"
                    helperText={
                      draftPreset && !draftNotes.trim()
                        ? `Saved as: "${draftPreset}"`
                        : 'Overrides preset when filled'
                    }
                  />
                </Stack>
              </Paper>
            ) : ex.reasonPreset || ex.reasonText ? (
              <Alert severity="success" icon={false}>
                <Typography variant="subtitle2">Curator note</Typography>
                <Typography variant="body2">{ex.reasonPreset ?? ex.reasonText}</Typography>
              </Alert>
            ) : null}

            {saveMutation.isError ? (
              <Alert severity="error">Could not save changes. Try again.</Alert>
            ) : null}

            <Button
              variant="contained"
              size="small"
              disabled={!isDirty || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>

            <TranscriptEvidenceBlock
              segments={transcriptSegments}
              windowStart={evidenceWindow.start}
              windowEnd={evidenceWindow.end}
              fallbackText={ex.evidence?.transcriptText}
            />

            {frameEvidenceItems.length > 0 ? (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {pgmCue ? 'Block frames (select for PGM)' : `Frame evidence (${frameEvidenceItems.length})`}
                </Typography>
                {pgmCue && ex ? (
                  <SelectableFrameEvidenceGallery
                    frames={frameEvidenceItems}
                    contentUrl={ex.contentUrl}
                    contentId={ex.contentId}
                    mentionId={ex.mentionId}
                    learningExampleId={ex._id}
                    pgmSelections={pgmSelections}
                    committedSourceKeys={pgmCommittedSourceKeys}
                    pgmCue={pgmCue}
                    onPgmDataChanged={refreshExampleDetail}
                  />
                ) : (
                  <FrameEvidenceGallery frames={frameEvidenceItems} contentUrl={ex.contentUrl} />
                )}
              </Box>
            ) : pgmCue ? (
              <Alert severity="info" icon={false}>
                No block frames yet. Run{' '}
                <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                  yarn explorer:long:visual-analyze --content-id {ex.contentId}
                </Typography>{' '}
                on the episode first.
              </Alert>
            ) : (
              <Alert severity="info" icon={false}>
                No frame stored yet. Run{' '}
                <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                  yarn explorer:long:build-learnings --mention-id {ex.mentionId}
                </Typography>{' '}
                to extract one.
              </Alert>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                component={RouterLink}
                to={`${ROUTES_NAMES.PODCAST_CONTENT_ANALYSIS.replace(':podcastId', ex.podcastId)}`}
                endIcon={<OpenInNewIcon fontSize="small" />}
              >
                Open podcast review
              </Button>
              {ex.contentUrl ? (
                <Link href={ex.contentUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                  Episode
                </Link>
              ) : null}
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              dedupe: {ex.dedupeKey}
            </Typography>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

const PAGE_SIZE_OPTIONS = PODCAST_LEARNINGS_PAGE_SIZE_OPTIONS;

const EVIDENCE_FILTER_OPTIONS: { value: PodcastLearningsEvidenceFilter; label: string }[] = [
  { value: '', label: 'Any' },
  { value: 'transcript', label: 'Transcript text' },
  { value: 'frame', label: 'Frame image' },
  { value: 'both', label: 'Transcript + frame' },
  { value: 'none', label: 'No evidence stored' },
];

export default function PodcastLearningsExplorer() {
  const {
    tab,
    setTab,
    podcastId,
    setPodcastId,
    flow,
    setFlow,
    failureMode,
    setFailureMode,
    outcome,
    setOutcome,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    evidenceFilter,
    setEvidenceFilter,
    includeFilter,
    setIncludeFilter,
    pgmCue,
    setPgmCue,
    rowsPerPage,
    setRowsPerPage,
  } = usePodcastLearningsStore();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pgmMentionOriginFilter, setPgmMentionOriginFilter] = useState<'' | MentionOrigin>('');
  const [pendingIncludeIds, setPendingIncludeIds] = useState<Set<string>>(new Set());
  const [pendingExcludeIds, setPendingExcludeIds] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  useEffect(() => {
    setPage(0);
  }, [podcastId, flow, failureMode, outcome, dateFrom, dateTo, evidenceFilter, includeFilter, pgmCue, pgmMentionOriginFilter, tab]);

  const podcastsQuery = useQuery({
    queryKey: ['podcast-learnings-podcasts'],
    queryFn: () => apiService.getPodcastLearningsPodcasts(),
  });

  const statsQuery = useQuery({
    queryKey: ['podcast-learnings-stats', podcastId],
    queryFn: () => apiService.getPodcastLearningsStats(podcastId || undefined),
  });

  const examplesQuery = useQuery({
    queryKey: [
      'podcast-learnings-examples',
      podcastId,
      flow,
      failureMode,
      outcome,
      dateFrom,
      dateTo,
      evidenceFilter,
      includeFilter,
      page,
      rowsPerPage,
    ],
    queryFn: () =>
      apiService.getPodcastLearningExamples({
        podcastId: podcastId || undefined,
        flow: flow || undefined,
        failureMode: failureMode || undefined,
        outcome: outcome || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        evidence: evidenceFilter || undefined,
        includeInAlgorithm:
          includeFilter === 'yes' ? 'true' : includeFilter === 'no' ? 'false' : undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }),
  });

  const includeMutation = useMutation({
    mutationFn: ({ id, includeInAlgorithm }: { id: string; includeInAlgorithm: boolean }) =>
      apiService.updatePodcastLearningExampleInclusion(id, includeInAlgorithm),
    onMutate: ({ id }) => {
      setPendingIncludeIds((prev) => new Set(prev).add(id));
    },
    onSettled: (_data, _err, { id }) => {
      setPendingIncludeIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcast-learnings-examples'] });
      queryClient.invalidateQueries({ queryKey: ['podcast-learnings-stats'] });
      queryClient.invalidateQueries({ queryKey: ['podcast-learnings-profile'] });
      queryClient.invalidateQueries({ queryKey: ['podcast-learning-example'] });
    },
  });

  const excludeMutation = useMutation({
    mutationFn: ({ id, excludeFromLearning }: { id: string; excludeFromLearning: boolean }) =>
      apiService.updatePodcastLearningExampleExclusion(id, excludeFromLearning),
    onMutate: ({ id }) => {
      setPendingExcludeIds((prev) => new Set(prev).add(id));
    },
    onSettled: (_data, _err, { id }) => {
      setPendingExcludeIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['podcast-learnings-examples'] });
      queryClient.invalidateQueries({ queryKey: ['podcast-learnings-stats'] });
      queryClient.invalidateQueries({ queryKey: ['podcast-learning-example'] });
    },
  });

  const handleExcludeToggle = (row: PodcastLearningExampleRow, checked: boolean) => {
    excludeMutation.mutate({ id: row._id, excludeFromLearning: checked });
  };

  const handleIncludeToggle = (row: PodcastLearningExampleRow, checked: boolean) => {
    includeMutation.mutate({ id: row._id, includeInAlgorithm: checked });
  };

  const profileQuery = useQuery({
    queryKey: ['podcast-learnings-profile', podcastId],
    queryFn: () => apiService.getPodcastLearningProfile(podcastId),
    enabled: Boolean(podcastId) && tab === 1,
  });

  const gapsQuery = useQuery({
    queryKey: ['podcast-learnings-gaps', podcastId],
    queryFn: () =>
      apiService.getPodcastLearningFeedbackGaps({
        podcastId: podcastId || undefined,
        limit: 50,
      }),
    enabled: tab === 2,
  });

  const pgmFixtureStatusQuery = useQuery({
    queryKey: ['pgm-fixture-status', pgmCue],
    queryFn: () => apiService.getPgmFixtureStatus(pgmCue),
    enabled: tab === 3,
  });

  const pgmExamplesQuery = useQuery({
    queryKey: [
      'podcast-learnings-pgm-examples',
      podcastId,
      pgmCue,
      pgmMentionOriginFilter,
      dateFrom,
      dateTo,
      page,
      rowsPerPage,
    ],
    queryFn: () =>
      apiService.getPodcastLearningExamples({
        podcastId: podcastId || undefined,
        pgmCue,
        hasBlockFrames: true,
        mentionOrigin: pgmMentionOriginFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }),
    enabled: tab === 3,
  });

  const stats = statsQuery.data;
  const flowOptions = useMemo(
    () =>
      Object.entries(stats?.flowLabels ?? {})
        .filter(([value]) => value !== 'placement')
        .map(([value, label]) => ({ value, label })),
    [stats?.flowLabels],
  );
  const failureModeOptions = useMemo(
    () =>
      Object.entries(stats?.failureModeLabels ?? {}).map(([value, label]) => ({ value, label })),
    [stats?.failureModeLabels],
  );
  const podcastOptions = podcastsQuery.data?.podcasts ?? [];

  const examples = examplesQuery.data?.examples ?? [];
  const examplesTotal = examplesQuery.data?.total ?? 0;
  const pgmExamples = pgmExamplesQuery.data?.examples ?? [];
  const pgmExamplesTotal = pgmExamplesQuery.data?.total ?? 0;
  const pgmSourceKeys = pgmFixtureStatusQuery.data?.sourceKeys ?? [];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <SchoolIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Podcast Learnings Explorer
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Browse curator feedback digested into learning examples — grouped by flow and failure mode,
        with transcript and frame evidence. Physical placement mentions are excluded from learnings.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              label="Podcast"
              size="small"
              fullWidth
              value={podcastId}
              onChange={(e) => setPodcastId(e.target.value)}
              disabled={podcastsQuery.isLoading}
            >
              <MenuItem value="">All podcasts</MenuItem>
              {podcastOptions.map((p) => (
                <MenuItem key={p.podcastId} value={p.podcastId}>
                  {p.title} ({p.exampleCount})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="Flow"
              size="small"
              fullWidth
              value={flow}
              onChange={(e) => setFlow(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {flowOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              label="Failure mode"
              size="small"
              fullWidth
              value={failureMode}
              onChange={(e) => setFailureMode(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {failureModeOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="Outcome"
              size="small"
              fullWidth
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="Evidence"
              size="small"
              fullWidth
              value={evidenceFilter}
              onChange={(e) =>
                setEvidenceFilter(e.target.value as PodcastLearningsEvidenceFilter)
              }
            >
              {EVIDENCE_FILTER_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'any'} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              label="Included"
              size="small"
              fullWidth
              value={includeFilter}
              onChange={(e) => setIncludeFilter(e.target.value as PodcastLearningsIncludeFilter)}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="yes">Included in algorithm</MenuItem>
              <MenuItem value="no">Not included</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              label="Reviewed from"
              size="small"
              fullWidth
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              label="Reviewed to"
              size="small"
              fullWidth
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {statsQuery.isLoading ? (
        <CircularProgress size={24} sx={{ mb: 2 }} />
      ) : stats ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <StatCard label="Learning examples" value={stats.totalExamples} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <StatCard label="Feedback mentions" value={stats.feedbackMentionCount} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <StatCard
              label="Coverage gap"
              value={Math.max(0, stats.feedbackMentionCount - stats.totalExamples)}
              sub="feedback without example"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <StatCard label="With frame" value={stats.withFrame} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <StatCard
              label="In algorithm"
              value={stats.includedInAlgorithm}
              sub="curator-selected"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <StatCard label="Podcast profiles" value={stats.profileCount} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <StatCard
              label="Rejected"
              value={stats.byOutcome['rejected'] ?? 0}
              sub={`confirmed ${stats.byOutcome['confirmed'] ?? 0}`}
            />
          </Grid>
        </Grid>
      ) : null}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Examples (${examplesQuery.data?.total ?? '…'})`} />
        <Tab label="Podcast profile" disabled={!podcastId} />
        <Tab label="Feedback gaps" />
        <Tab label={`PGM fixtures (${pgmExamplesQuery.data?.total ?? '…'})`} />
        <Tab label="PGM audit" />
      </Tabs>

      {tab === 0 && (
        <Paper variant="outlined">
          {examplesQuery.isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : examples.length === 0 ? (
            <Box sx={{ p: 4 }}>
              <Typography color="text.secondary">
                No learning examples yet. Confirm/reject mentions in review, or run{' '}
                <Typography component="span" sx={{ fontFamily: 'monospace' }}>
                  yarn explorer:long:build-learnings --date-from …
                </Typography>
              </Typography>
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Tooltip title="Exclude from auto learning memory">
                        <span>Exclude</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell padding="checkbox">
                      <Tooltip title="Include in algorithm prompts (manual path)">
                        <span>Include</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>Reviewed</TableCell>
                    <TableCell>Outcome</TableCell>
                    <TableCell>Origin</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Flow</TableCell>
                    <TableCell>Failure mode</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Evidence</TableCell>
                    <TableCell>Podcast</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {examples.map((row: PodcastLearningExampleRow) => (
                    <TableRow
                      key={row._id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: row.excludeFromLearning
                          ? 'error.light'
                          : row.includeInAlgorithm
                            ? 'action.hover'
                            : undefined,
                      }}
                      onClick={() => setSelectedId(row._id)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={Boolean(row.excludeFromLearning)}
                          disabled={pendingExcludeIds.has(row._id)}
                          onChange={(e) => handleExcludeToggle(row, e.target.checked)}
                          inputProps={{ 'aria-label': `Exclude ${row.brandName} from learning` }}
                        />
                      </TableCell>
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={Boolean(row.includeInAlgorithm)}
                          disabled={pendingIncludeIds.has(row._id)}
                          onChange={(e) => handleIncludeToggle(row, e.target.checked)}
                          inputProps={{ 'aria-label': `Include ${row.brandName} in algorithm` }}
                        />
                      </TableCell>
                      <TableCell>
                        {row.reviewedAt
                          ? new Date(row.reviewedAt).toLocaleDateString('es-CL')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.outcome}
                          color={OUTCOME_COLOR[row.outcome] ?? 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <MentionOriginChip
                          origin={row.mentionOrigin}
                          label={row.mentionOriginLabel}
                          correctedFields={row.correctedFields}
                        />
                      </TableCell>
                      <TableCell>{row.brandName}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={row.flowLabel ?? row.flow} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>
                        <Typography variant="body2" noWrap title={row.failureModeLabel}>
                          {row.failureModeLabel ?? row.failureMode}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={row.reasonPreset ?? row.reasonText}>
                          {row.reasonPreset ?? row.reasonText ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {row.evidence?.transcriptText ? (
                            <Chip size="small" label="text" variant="outlined" />
                          ) : null}
                          {(row.frameUrls?.length ?? 0) > 0 || row.frameUrl ? (
                            <Chip
                              size="small"
                              label={
                                (row.frameUrls?.length ?? 0) > 1
                                  ? `${row.frameUrls!.length} frames`
                                  : 'frame'
                              }
                              color="info"
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 140 }}>
                        <Typography variant="body2" noWrap title={row.podcastTitle ?? row.podcastId}>
                          {row.podcastTitle ?? row.podcastId.slice(-6)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={examplesTotal}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
                labelRowsPerPage="Rows per page"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
              />
            </>
          )}
        </Paper>
      )}

      {tab === 1 && podcastId && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          {profileQuery.isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {profileQuery.data?.profile?.podcastTitle ?? podcastId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cache version {profileQuery.data?.profile?.cacheVersion ?? '—'}
                </Typography>
              </Box>
              <CategoryTable rows={profileQuery.data?.profile?.categoryRows ?? []} />
              <Divider />
              <Typography variant="subtitle2">Recent examples</Typography>
              <Stack spacing={1}>
                {(profileQuery.data?.recentExamples ?? []).map((ex) => (
                  <Paper
                    key={ex._id}
                    variant="outlined"
                    sx={{ p: 1.5, cursor: 'pointer' }}
                    onClick={() => setSelectedId(ex._id)}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={ex.outcome} color={OUTCOME_COLOR[ex.outcome]} />
                      <Typography variant="body2">
                        {ex.brandName} · {ex.failureModeLabel}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          )}
        </Paper>
      )}

      {tab === 2 && (
        <Paper variant="outlined">
          {gapsQuery.isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (gapsQuery.data?.gaps ?? []).length === 0 ? (
            <Box sx={{ p: 4 }}>
              <Typography color="text.secondary">
                All recent feedback has matching learning examples.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Updated</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Feedback</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Flow hint</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gapsQuery.data!.gaps.map((g) => (
                  <TableRow key={g._id}>
                    <TableCell>
                      {g.updatedAt ? new Date(g.updatedAt).toLocaleDateString('es-CL') : '—'}
                    </TableCell>
                    <TableCell>{g.brandName}</TableCell>
                    <TableCell>
                      <Chip size="small" label={g.feedback?.status ?? '?'} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" noWrap>
                        {g.feedback?.reason ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {[g.evidenceType, g.visualCue, g.placementType].filter(Boolean).join(' · ') ||
                        '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {tab === 3 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick specific block frames from visual learning examples and add them as positive PGM
            regression cases. Only frames with timestamps from visual-analyze block galleries are
            selectable.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
            {PGM_CUE_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                color={pgmCue === opt.value ? 'primary' : 'default'}
                variant={pgmCue === opt.value ? 'filled' : 'outlined'}
                onClick={() => setPgmCue(opt.value)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            {pgmFixtureStatusQuery.data ? (
              <Chip
                size="small"
                variant="outlined"
                label={`${pgmFixtureStatusQuery.data.caseIds.length} committed (${pgmCue})`}
              />
            ) : null}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
            {MENTION_ORIGIN_FILTER_OPTIONS.map((opt) => (
              <Chip
                key={opt.value || 'all'}
                size="small"
                label={opt.label}
                color={pgmMentionOriginFilter === opt.value ? 'primary' : 'default'}
                variant={pgmMentionOriginFilter === opt.value ? 'filled' : 'outlined'}
                onClick={() => setPgmMentionOriginFilter(opt.value)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>

          {pgmExamplesQuery.isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : pgmExamples.length === 0 ? (
            <Box sx={{ p: 4 }}>
              <Typography color="text.secondary">
                No learning examples with block frames for {pgmCue}. Run visual-analyze on episodes
                first, or try another cue filter.
              </Typography>
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reviewed</TableCell>
                    <TableCell>Outcome</TableCell>
                    <TableCell>Origin</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Segment</TableCell>
                    <TableCell align="right">Block frames</TableCell>
                    <TableCell>PGM status</TableCell>
                    <TableCell>Podcast</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pgmExamples.map((row) => {
                    const committed = countCommittedPgmFrames(row.contentId, pgmSourceKeys);
                    const blockCount = row.blockFrameCount ?? 0;
                    return (
                      <TableRow
                        key={row._id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedId(row._id)}
                      >
                        <TableCell>
                          {row.reviewedAt
                            ? new Date(row.reviewedAt).toLocaleDateString('es-CL')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.outcome}
                            color={OUTCOME_COLOR[row.outcome] ?? 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <MentionOriginChip
                            origin={row.mentionOrigin}
                            label={row.mentionOriginLabel}
                            correctedFields={row.correctedFields}
                          />
                        </TableCell>
                        <TableCell>{row.brandName}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {formatTime(row.segmentStart)}–{formatTime(row.segmentEnd)}
                        </TableCell>
                        <TableCell align="right">{blockCount}</TableCell>
                        <TableCell>
                          {committed > 0 ? (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label={`${committed}/${blockCount} in PGM`}
                            />
                          ) : (
                            <Chip size="small" variant="outlined" label="0 committed" />
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 140 }}>
                          <Typography variant="body2" noWrap title={row.podcastTitle ?? row.podcastId}>
                            {row.podcastTitle ?? row.podcastId.slice(-6)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={pgmExamplesTotal}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
                labelRowsPerPage="Rows per page"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
              />
            </>
          )}
        </Paper>
      )}

      {tab === 4 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            PGM audit workspace
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review full-episode frame sweeps, mark incorrect PGM passes, and curate positive/negative fixtures.
            Opens in a dedicated full-page view with all visual cue verdicts per frame.
          </Typography>
          <Button component={RouterLink} to={ROUTES_NAMES.PGM_AUDIT} variant="contained">
            Open PGM audit
          </Button>
        </Paper>
      )}

      <ExampleDetailDrawer
        exampleId={selectedId}
        onClose={() => setSelectedId(null)}
        pgmCue={tab === 3 ? pgmCue : undefined}
        onPgmFixturesChanged={async () => {
          await pgmFixtureStatusQuery.refetch();
          await pgmExamplesQuery.refetch();
        }}
      />
    </Box>
  );
}
